import { ContentService } from '@blk/cwp-ng-framework';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as unzip from 'lodash/unzip';

import { CellUtil } from '../../utils';
import { CellData } from '../cell-data';
import { ColumnConfig } from '../column-config';
import { ColumnText } from '../column-text';
import { ColumnExtractor } from '../extractors/column-extractor';
import { StoreColumnExtractor } from '../extractors/store-column-extractor';
import { SeriesMapper } from '../series-mappers/series-mapper.interface';
import { TableData } from '../table-data';
import { AsIsTableDataTransform } from '../table-data-transform/as-is-table-data-transform';
import { TableDataTransform } from '../table-data-transform/table-data-transform';
import { MultipleSeriesExtractionExtractorConfig } from './multiple-series-extractor-config';

/**
 * Extractor used to extract data from multiple stores and group it per portfolio.
 * The extractor combines the values from the 2 data stores and maps them on the id at the portfolio level and ticker at the security level.
 * If both stores have the same data, the value in the second data store is going to be mapped.
 *
 * The extractor works for only 2 store mappers.
 * If more than 2 store mappers are provided, only the first 2 are taken into consideration.
 */
export class TablePerPortfolioMultipleSeriesExtractorConfig extends MultipleSeriesExtractionExtractorConfig {
	constructor(
		mappers: SeriesMapper[],
		public firstColumnTitle: ColumnText,
		public readonly columns: ColumnConfig<ColumnExtractor>[],
		portfolioName: ColumnConfig<StoreColumnExtractor>,
		tableDataTransform: TableDataTransform = AsIsTableDataTransform.instance,
		public childColumnKey: string = 'ticker' // Extract common data from 2 stores based on childColumnKey (pass the key to match in child object from both stores)
	) {
		super(portfolioName, mappers, tableDataTransform);
	}

	public storeDependencies(): string[] {
		const storeDep = [];
		this.mappers.forEach(mapper => storeDep.push(mapper.getStoreDependencies()));

		return storeDep;
	}

	public extract(storeLookup: Map<string, any[]>, contentSvc: ContentService): TableData[] {
		// get portfolio data array from the provided mappers
		const portfolioDataArray: any[] = [];
		this.mappers.forEach(mapper => {
			const dataArg = this.getPortfolioDataArgs(mapper, storeLookup);
			portfolioDataArray.push(dataArg);
		});

		// combine the portfolio data array values based on the id
		const combinedPortfolioDataValuesArray: any[] = [];
		// combine the values for the first two stores
		for (let i = 0; i < portfolioDataArray[0].length; i++) {
			if (portfolioDataArray.length > 1) {
				combinedPortfolioDataValuesArray.push(...this.getCombinedValues(portfolioDataArray[0][i], portfolioDataArray[1][i], 'id'));
			} else {
				combinedPortfolioDataValuesArray.push(...portfolioDataArray[0][i]);
			}
		}

		// map each portfolio data to a TableData object
		return combinedPortfolioDataValuesArray.map(portfolioData => {
			const securitiesMappedValues1: any[] = [];
			const securitiesMappedValues2: any[] = [];

			// get the securities values for each portfolio data (combined portfolio values)
			this.mappers.forEach((mapper, index) => {
				const res = mapper.mapper(portfolioData);
				if (index === 0) {
					securitiesMappedValues1.push(res);
				} else {
					securitiesMappedValues2.push(res);
				}
			});

			// combine securities values per portfolio based on the ticker
			const combinedSecuritiesValuesArray: any[] = [];
			for (let i = 0; i < securitiesMappedValues1.length; i++) {
				combinedSecuritiesValuesArray.push(...this.getCombinedValues(securitiesMappedValues1[i], securitiesMappedValues2[i], this.childColumnKey));
			}

			// build cells for the combined securities values array
			const cells: CellData[][] = [];
			combinedSecuritiesValuesArray.forEach(data => {
				const parent = this.columns.map(column => {
					// translate the column config (label and info bubble) at the cell level
					column = CellUtil.translateColumnConfig(column, contentSvc);

					const value = column.extractor.extractor()(data);
					return new CellData(column, value, column.labelFormatter.format(value, contentSvc), null, this.generateAttributes(value, column, contentSvc));
				});

				cells.push(parent);
			});

			// build portfolioNames, columnName, tableData and return the transformed TableData
			const portfolioNames: CellData[] = this.extractStoreColumn(this.portfolioName, storeLookup, contentSvc);
			const columnNames: CellData[] = this.generateColumnHeaders(this.columns, contentSvc);

			const tableData = new TableData(columnNames, portfolioNames, cells, portfolioNames, this.translateColumnText(this.firstColumnTitle, contentSvc));
			const transformedTableData: TableData = this.tableDataTransform.transform(tableData, contentSvc);

			return transformedTableData;
		});
	}

	public getDataPointPath(): any {
		return '';
	}

	/**
	 * Combines the provided 2 arrays based on provided column.
	 *
	 * @private
	 * @param {*} array1
	 * @param {*} array2
	 * @param {string} columnToMatch
	 * @returns {any[]}
	 * @memberof TablePerPortfolioMultipleSeriesExtractorConfig
	 */
	private getCombinedValues(array1: any, array2, columnToMatch: string): any[] {
		if (isEmpty(array1) && !isEmpty(array2)) {
			return array2;
		}

		if (!isEmpty(array1) && isEmpty(array2)) {
			return array1;
		}

		const combinedValuesArray: any[] = [];
		array1.forEach(value1 => {
			const id1 = get(value1, columnToMatch);

			array2.forEach(value2 => {
				const id2 = get(value2, columnToMatch);

				if (id1 === id2) {
					const object4 = { ...value1, ...value2 };
					combinedValuesArray.push(object4);
				}
			});
		});

		return combinedValuesArray;
	}

	/**
	 * Returns the portfolio data values based of the provided mapper and storeLookup.
	 *
	 * @private
	 * @param {SeriesMapper} mapper
	 * @param {Map<string, any[]>} storeLookup
	 * @returns {*}
	 * @memberof TablePerPortfolioMultipleSeriesExtractorConfig
	 */
	private getPortfolioDataArgs(mapper: SeriesMapper, storeLookup: Map<string, any[]>): any {
		const storeLookupConfig = mapper.getStoreDependencies().map(config => storeLookup.get(config));
		return unzip(storeLookupConfig);
	}
}
